import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HardHat, ArrowLeft, Calculator, ShieldAlert, 
  FileCheck, Siren, Activity, ClipboardCheck, Users, Construction, Gavel, CalendarCheck, MessageSquareMore, ShieldCheck, FileText, Scale
} from 'lucide-react';

// --- IMÁGENES DEL CARRUSEL (Sección Dolor) ---
const SST_CAROUSEL_IMAGES = [
  "https://zaphy.com.co/wp-content/uploads/2021/04/SGSST-Banner-05.png",
  "https://zaphy.com.co/wp-content/uploads/2026/01/IMG-20250227-WA0082.jpg",
  "https://zaphy.com.co/wp-content/uploads/2026/01/sst-scaled.jpg",
  "https://zaphy.com.co/wp-content/uploads/2026/01/sst-3.jpg",
  "https://zaphy.com.co/wp-content/uploads/2026/01/sst-2.jpg"
];

const SST = ({ language }) => { 
  
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % SST_CAROUSEL_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // --- ESTRUCTURA DE CONTENIDOS EVOLUCIONADA (ALEX HORMOZI + RESOLUCIÓN 0312 + SEO DE ALTA DENSIDAD) ---
  const t = {
    es: {
      back: "Volver al Inicio",
      heroTag: "Outsourcing de Seguridad y Salud en el Trabajo",
      heroTitle: "Consultoría y Gestión del SG-SST:",
      heroSubtitle: "Tu Empresa Blindada y al Día",
      heroDesc: "Implementamos, administramos y ejecutamos tu Sistema de Gestión (SG-SST) bajo el Decreto 1072 y la Resolución 0312. En Zaphy asumimos el liderazgo absoluto de tu área de seguridad laboral para proteger tu patrimonio.",
      heroBtnQuote: "Cotizar SG-SST",
      heroBtnWhatsApp: "Escríbenos por WhatsApp",
      imgBadgeTitle: "Responsabilidad",
      imgBadgeDesc: "Asumida por Zaphy",
      painTag: "Riesgo Inminente",
      painTitle: "¿Estás listo para una visita del Ministerio de Trabajo?",
      painDesc: "Muchos gerentes creen que el SG-SST es solo llenar carpetas de papel. La realidad es que un accidente de trabajo grave sin los controles de ley correctos puede acarrear multas millonarias, demandas por culpa patronal e incluso sanciones penales para el representante legal.",
      painHighlight: "Nosotros no somos asesores externos que solo dan recomendaciones. En Zaphy asumimos contractualmente el frente operativo, nos convertimos en los responsables del área y respondemos técnicamente ante los entes de control.",
      painBadgeTitle: "Resultado",
      painBadgeDesc: "Cero No Conformidades",
      
      // Banner de Traspaso de Carga Legal
      bannerTitle: "Transferencia de Responsabilidad Real",
      bannerDesc: "Al contratarnos, delegas el 100% de la carga técnica y jurídica. Diseñamos la Matriz de Peligros, capacitamos a tus Brigadas y firmamos como administradores del sistema con licencia vigente. Si hay una auditoría del Ministerio o de la ARL, nosotros ponemos la cara por ti.",
      
      gridTitle: "Alcance de Nuestro Outsourcing de SST",
      gridSubtitle: "Diseño, ejecución y mantenimiento continuo avalado por ingenieros especialistas con licencias técnico-legales vigentes.",
      cards: [
        { title: "Diseño e Ingeniería de Sistemas", desc: "Estructuración completa de la documentación base bajo el Decreto 1072 y la Resolución 0312 (Estándares Mínimos) según tu sector y nivel de riesgo." },
        { title: "Matriz de Peligros Dinámica", desc: "Identificación real en campo bajo la metodología GTC 45 y valoración de riesgos críticos para priorizar controles de ingeniería efectivos." },
        { title: "Comités y Brigadas de Ley", desc: "Conformación, capacitación periódica y actas obligatorias para el COPASST, Comité de Convivencia Laboral y Brigadas de Emergencia." },
        { title: "Investigación de Siniestros (FURAT)", desc: "Atención inmediata, reporte técnico ante la ARL y modelado legal de causas de accidentes para evitar fallos de responsabilidad patronal." },
        { title: "Vigilancia de la Salud Laboral", desc: "Seguimiento riguroso a exámenes médicos ocupacionales de ingreso, periódicos y de retiro, articulados con profesiogramas técnicos." },
        { title: "Plan de Choque ante Inspecciones", desc: "Defensa técnica y jurídica presencial inmediata si recibes una visita sorpresa o requerimiento formal del Ministerio de Trabajo." },
        { title: "Co-Responsabilidad Firmada", desc: "No te dejamos con carpetas archivadas. Respondemos contractualmente por la ejecución diaria del sistema de gestión ante entes de control." },
        { title: "Auditoría Interna y Rendición", desc: "Simulacros obligatorios anuales, revisión gerencial de estándares mínimos y radicación digital del reporte de cumplimiento ante el Ministerio." }
      ],
      seoTitle: 'Preguntas Frecuentes sobre la Implementación del SG-SST para PyMEs',
      seoSubtitle: 'Resolvemos tus dudas sobre auditorías del Ministerio de Trabajo, responsabilidades de la ARL y el servicio experto de Zaphy.',
      faqs: [
        { q: '¿Qué empresas están obligadas a implementar el SG-SST en Colombia?', a: 'Según el Decreto 1072 de 2015, toda empresa o empleador en Colombia, sin importar si tiene 1 o más de 100 trabajadores (independientemente de su forma de contratación por prestación de servicios o término fijo), debe diseñar e implementar el Sistema de Gestión de la Seguridad y Salud en el Trabajo.' },
        { q: '¿A qué multas se expone mi PyME si no tengo el sistema de gestión al día?', a: 'El Ministerio de Trabajo puede imponer sanciones económicas progresivas que superan los 100 salarios mínimos legales vigentes por el incumplimiento de los Estándares Mínimos de la Resolución 0312. Además, en caso de un accidente de trabajo grave o mortal, la gerencia puede enfrentar demandas por culpa patronal con indemnizaciones millonarias.' },
        { q: '¿Cuál es la diferencia entre el servicio de Zaphy y un prevencionista o consultor tradicional?', a: 'Los asesores tradicionales suelen limitarse a redactar documentos que se quedan archivados en carpetas físicas. En Zaphy transferimos la carga administrativa: ejecutamos las capacitaciones de forma dinámica, nos convertimos en los responsables del área, firmamos la co-responsabilidad legal del sistema de gestión y te brindamos defensa técnica presencial inmediata ante cualquier auditoría sorpresa.' },
        { q: '¿Qué son los Estándares Mínimos de la Resolución 0312 y cómo se aplican según mi riesgo?', a: 'La Resolución 0312 clasifica las obligaciones según el número de empleados y el nivel de riesgo (I a V) ante la ARL. Las empresas con menos de 10 trabajadores en riesgo I, II o III solo deben cumplir con 7 estándares básicos; las de 11 a 50 trabajadores en riesgo bajo deben cumplir con 21 estándares; mientras que las organizaciones con más de 50 empleados, o aquellas de cualquier tamaño que estén catalogadas en riesgo alto (Riesgo IV o V), están obligadas por ley a implementar los 60 estándares mínimos completos de forma estricta.' },
        { q: '¿El servicio incluye el acompañamiento y las capacitaciones para el COPASST y Brigadas?', a: 'Sí, nos encargamos de todo el ciclo: desde el proceso de votación digital y actas de conformación, hasta las capacitaciones especializadas obligatorias en primeros auxilios, control de incendios y evacuación, asegurando que tus comités de ley dejen evidencias válidas ante cualquier requerimiento gubernamental.' }
      ],
      ctaTitle: "La seguridad no es un gasto, es blindaje patrimonial",
      ctaDesc: "Protege lo más valioso de tu empresa: tu gente y tu tranquilidad jurídica. Cumple la norma hoy mismo con un sistema ágil, digital y a tu medida.",
      ctaBtnQuote: "Cotizar Implementación",
      ctaBtnSchedule: "Agendar Asesoría",
      ctaBtnWhatsApp: "Hablemos por WhatsApp"
    },
    en: {
      back: "Back to Home",
      heroTag: "Outsourcing of Occupational Health and Safety",
      heroTitle: "SG-SST Management & Consulting:",
      heroSubtitle: "Your Company Shielded and Up to Date",
      heroDesc: "We implement, manage, and execute your Management System (SG-SST) under Decree 1072 and Resolution 0312. At Zaphy, we assume absolute leadership of your workplace safety department to protect your business assets.",
      heroBtnQuote: "Quote SG-SST",
      heroBtnWhatsApp: "Chat on WhatsApp",
      imgBadgeTitle: "Liability",
      imgBadgeDesc: "Assumed by Zaphy",
      painTag: "Imminent Risk",
      painTitle: "Are you ready for a Ministry of Labor visit?",
      painDesc: "Many managers believe that SG-SST is just about filling physical paper binders. The reality is that a severe workplace accident without proper legal controls can lead to massive fines, employer-fault lawsuits, and criminal penalties for the legal representative.",
      painHighlight: "We are not external advisors who just give recommendations. At Zaphy, we contractually assume the operational workload, become responsible for the department, and respond technically before control entities.",
      painBadgeTitle: "Result",
      painBadgeDesc: "Zero Non-Conformities",
      
      bannerTitle: "Real Liability Transfer",
      bannerDesc: "By hiring us, you delegate 100% of the technical and legal burden. We design the Hazard Matrix, train your Brigades, and sign as system administrators with a valid license. If there is an audit by the Ministry or the ARL, we stand up for you.",
      
      gridTitle: "Scope of Our OHS Outsourcing",
      gridSubtitle: "Design, execution, and continuous maintenance backed by specialist engineers with valid technical-legal licenses.",
      cards: [
        { title: "System Engineering & Design", desc: "Complete structuring of baseline documentation under Decree 1072 and Resolution 0312 (Minimum Standards) tailored to your industry." },
        { title: "Dynamic Hazard Matrix", desc: "Real field identification using the GTC 45 methodology and critical risk assessment to prioritize effective engineering controls." },
        { title: "Legal Committees & Brigades", desc: "Formation, periodic training, and mandatory minutes for COPASST, Labor Coexistence Committee, and Emergency Brigades." },
        { title: "Workplace Incident Investigation", desc: "Immediate attention, technical reporting before the ARL (FURAT), and legal modeling of accident causes to avoid employer liability issues." },
        { title: "Occupational Health Surveillance", desc: "Rigorous monitoring of occupational medical exams (entry, periodic, and exit) perfectly articulated with technical job profiles." },
        { title: "Inspection Shock Plan", desc: "Immediate on-site technical and legal defense if you receive a surprise visit or formal requirement from the Ministry of Labor." },
        { title: "Signed Co-Responsibility Agreement", desc: "We don't leave you with archived files. We stand contractually responsible for the daily execution of your management system before control entities." },
        { title: "Internal Audits & Radication", desc: "Annual mandatory drills, minimum standards management review, and digital submission of the compliance report to the Ministry." }
      ],
      seoTitle: 'Frequently Asked Questions about SG-SST Implementation for PyMEs',
      seoSubtitle: 'We solve your doubts about Ministry of Labor audits, ARL responsibilities, and Zappings expert service.',
      faqs: [
        { q: 'Which companies are required to implement the SG-SST in Colombia?', a: 'According to Decree 1072 of 2015, every company or employer in Colombia, regardless of whether it has 1 or more than 100 workers (and independent of their hiring form), must design and implement the Occupational Health and Safety Management System.' },
        { q: 'What fines does my PyME face if my management system is not up to date?', a: 'The Ministry of Labor can impose progressive economic sanctions exceeding 100 minimum monthly wages for non-compliance with the Minimum Standards of Resolution 0312. In the event of a severe workplace incident, management can face employer-fault lawsuits.' },
        { q: 'What is the difference between Zaphys service and a traditional consultant?', a: 'Traditional advisors usually limit themselves to writing documents that get stored away in physical binders. At Zaphy we transfer the workload: we perform dynamic training, contractually sign the system legal co-responsibility, and provide immediate on-site technical defense.' },
        { q: 'What are the Minimum Standards of Resolution 0312 and how do they apply based on my risk?', a: 'Resolution 0312 classifies obligations based on headcount and risk level (I to V) before the ARL. Companies with fewer than 10 workers in risk I, II, or III only need to comply with 7 basic standards; those with 11 to 50 workers in low risk must meet 21 standards; while organizations with more than 50 employees, or those of any size classified as high risk (Risk IV or V), are legally required to fully implement all 60 complete minimum standards.' },
        { q: 'Does the service include support and training for COPASST and Brigades?', a: 'Yes, we take care of the entire cycle: from the digital voting process and minutes of formation, to specialized mandatory training in first aid, fire control, and evacuation, ensuring your legal committees leave valid evidence.' }
      ],
      ctaTitle: "Safety is not an expense, it's an asset shield",
      ctaDesc: "Protect your company's most valuable asset: your people and your legal peace of mind. Comply with regulations today with an agile, digital, and custom-made system.",
      ctaBtnQuote: "Quote Implementation",
      ctaBtnSchedule: "Schedule Advisory",
      ctaBtnWhatsApp: "Chat via WhatsApp"
    }
  };

  const text = t[language] || t.es;

  const handleWhatsAppRedirect = () => {
    const targetUrl = `https://api.whatsapp.com/send?phone=573114044203&text=${encodeURIComponent("¡Hola, Zaphy! 🚀 Estuve revisando el servicio de Outsourcing de SST en su web y quiero solicitar una asesoría directa sobre la co-responsabilidad legal del SG-SST para mi PyME.")}`;
    window.open(targetUrl, '_blank');
  };

  return (
    <div className="animate-[fadeIn_0.5s_ease-out] pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      
      {/* --- 1. HERO SECTION (CON LLAMADOS A LA ACCIÓN COMPLEMENTARIOS) --- */}
      <div className="relative bg-[#0F172A] text-white py-24 overflow-hidden mb-16">
         <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=1200&q=80)` }}></div>
         <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#E3FF00]/40"></div>
         
         <div className="max-w-7xl mx-auto px-4 relative z-10">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors font-medium">
               <ArrowLeft size={20} /> {text.back}
            </button>
            
            <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-[#E3FF00]/20 text-[#E3FF00] border border-[#E3FF00]/30">
                            <HardHat size={40} />
                        </div>
                        <span className="text-[#E3FF00] font-bold tracking-widest uppercase text-xs md:text-sm">{text.heroTag}</span>
                    </div>
                    
                    <h1 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
                    {text.heroTitle} <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E3FF00] to-lime-400">{text.heroSubtitle}</span>
                    </h1>
                    
                    <p className="text-lg text-gray-300 max-w-2xl leading-relaxed mb-8">
                    {text.heroDesc}
                    </p>

                    {/* DOBLE BOTÓN OPTIMIZADO (SÓLIDO E INVERTIDO) */}
                    <div className="flex flex-wrap gap-4">
                        <button 
                          onClick={() => navigate('/cotizador')} 
                          className="bg-[#E3FF00] text-black font-bold py-4 px-8 rounded-2xl shadow-lg shadow-[#E3FF00]/20 transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#d4ee00]"
                        >
                            <Calculator size={20}/> {text.heroBtnQuote}
                        </button>
                        
                        <button 
                          onClick={handleWhatsAppRedirect} 
                          className="bg-transparent border-2 border-[#E3FF00] text-[#E3FF00] font-bold py-4 px-8 rounded-2xl transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#E3FF00] hover:text-black shadow-lg shadow-[#E3FF00]/5"
                        >
                            <MessageSquareMore size={20}/> {text.heroBtnWhatsApp}
                        </button>
                    </div>
                </div>

                {/* IMAGEN HERO */}
                <div className="w-full max-w-md mt-10 md:mt-0">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-[#E3FF00] to-lime-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#0F172A]">
                             <img 
                                src="https://zaphy.com.co/wp-content/uploads/2026/01/sstzaphy.png" 
                                alt="Outsourcing de Seguridad y Salud en el Trabajo Colombia" 
                                className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent opacity-60"></div>
                            <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 p-3 rounded-xl flex items-center gap-3">
                                <div className="bg-[#E3FF00] p-1.5 rounded-full text-black"><ShieldAlert size={16} /></div>
                                <div>
                                    <p className="text-xs text-[#E3FF00] uppercase font-bold">{text.imgBadgeTitle}</p>
                                    <p className="text-sm font-bold text-white">{text.imgBadgeDesc}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
         </div>
      </div>

      {/* --- 2. EL PROBLEMA (CARRUSEL MÓVIL OPTIMIZADO) --- */}
      <div className="max-w-7xl mx-auto px-4 mb-16">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col md:flex-row items-center gap-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#E3FF00]/20 rounded-bl-[100px] z-0"></div>

              <div className="md:w-1/2 relative z-10">
                  <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full font-bold text-sm mb-4 border border-red-100">
                      <Siren size={16} /> {text.painTag}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{text.painTitle}</h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">{text.painDesc}</p>
                  <p className="font-bold text-gray-800 border-l-4 border-[#E3FF00] pl-4">{text.painHighlight}</p>
              </div>

              {/* Columna Derecha: Carrusel */}
              <div className="w-full md:w-1/2 relative z-10 mt-12 md:mt-0">
                  <div className="absolute -inset-4 bg-[#E3FF00]/30 rounded-[40px] transform -rotate-2 z-0"></div>
                  
                  <div className="relative z-10 rounded-[30px] shadow-lg w-full h-64 md:h-80 overflow-hidden bg-gray-200">
                      {SST_CAROUSEL_IMAGES.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Gestión de Riesgos Laborales ${idx + 1}`} 
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${idx === currentImage ? 'opacity-100' : 'opacity-0'}`}
                            onError={(e) => { e.target.src = "https://via.placeholder.com/800x600/E3FF00/000000?text=SST+Zaphy"; }}
                          />
                      ))}
                      
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                          {SST_CAROUSEL_IMAGES.map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentImage ? 'bg-[#E3FF00] w-4' : 'bg-white/50'}`}
                              />
                          ))}
                      </div>
                  </div>

                  <div className="absolute -bottom-6 -right-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-full text-green-600"><FileCheck size={24}/></div>
                      <div>
                          <p className="text-xs text-gray-500 font-bold uppercase">{text.painBadgeTitle}</p>
                          <p className="text-sm font-bold text-gray-900">{text.painBadgeDesc}</p>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      {/* --- BANNER INTERMEDIO: ASUMIMOS EL ÁREA (NUEVO BLOQUE ESTRATÉGICO) --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[30px] p-8 md:p-10 border border-slate-700/50 shadow-xl flex flex-col md:flex-row items-center gap-8">
            <div className="bg-[#E3FF00]/10 p-4 rounded-2xl text-[#E3FF00] shrink-0 border border-[#E3FF00]/20">
               <Scale size={40} />
            </div>
            <div>
               <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{text.bannerTitle}</h3>
               <p className="text-slate-300 text-sm md:text-base leading-relaxed">{text.bannerDesc}</p>
            </div>
         </div>
      </div>

      {/* --- 3. NUESTRA SOLUCIÓN INTEGRAL --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">{text.gridTitle}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.gridSubtitle}</p>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {text.cards.map((card, idx) => {
               const icons = [ClipboardCheck, Activity, Users, Siren, Construction, Gavel, ShieldCheck, FileText];
               const Icon = icons[idx];
               const borderColors = ['hover:border-yellow-400', 'hover:border-orange-400', 'hover:border-blue-400', 'hover:border-red-400', 'hover:border-purple-400', 'hover:border-green-400', 'hover:border-emerald-400', 'hover:border-teal-400'];
               const originalIconColors = ['bg-yellow-50 text-yellow-600', 'bg-orange-50 text-orange-600', 'bg-blue-50 text-blue-600', 'bg-red-50 text-red-600', 'bg-purple-50 text-purple-600', 'bg-green-50 text-green-600', 'bg-emerald-50 text-emerald-600', 'bg-teal-50 text-teal-600'];
               return (
                  <div key={idx} className={`bg-white p-6 rounded-3xl border border-gray-100 hover:shadow-xl transition-all group ${borderColors[idx]}`}>
                      <div className={`w-12 h-12 ${originalIconColors[idx]} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                          <Icon size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{card.title}</h3>
                      <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{card.desc}</p>
                  </div>
               );
            })}
         </div>
      </div>

      {/* --- 3.5: BLOQUE DE PREGUNTAS FRECUENTES (SEO BOOST SEMÁNTICO) --- */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
         <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{text.seoTitle}</h2>
            <p className="text-gray-500 text-xs md:text-sm">{text.seoSubtitle}</p>
         </div>
         <div className="space-y-4">
            {text.faqs.map((faq, idx) => (
               <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-slate-200 transition-all">
                  <h3 className="text-sm md:text-base font-bold text-gray-950 mb-2 flex items-start gap-2">
                     <span className="text-lime-600 shrink-0">❓</span> {faq.q}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm leading-relaxed pl-6">{faq.a}</p>
               </div>
            ))}
         </div>
      </div>

      {/* --- 4. CTA FINAL --- */}
      <div className="max-w-4xl mx-auto px-4 text-center">
         <div className="bg-gradient-to-br from-[#0F172A] to-[#4c5500] rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E3FF00]/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
            
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 relative z-10">{text.ctaTitle}</h2>
            <p className="text-[#f7ffb8] text-base md:text-lg mb-8 max-w-2xl mx-auto relative z-10">{text.ctaDesc}</p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
                <button 
                  onClick={() => navigate('/cotizador')} 
                  className="bg-[#E3FF00] text-black font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-[#d4ee00] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <Calculator size={20}/>
                    {text.ctaBtnQuote}
                </button>

                <button 
                    onClick={handleWhatsAppRedirect}
                    className="bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-emerald-700 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <MessageSquareMore size={20}/>
                    {text.ctaBtnWhatsApp}
                </button>

                <a 
                  href="https://calendar.app.google/UYZ2ERubo552yRg19" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-transparent border-2 border-[#E3FF00] text-[#E3FF00] font-bold py-4 px-8 rounded-xl hover:bg-[#E3FF00]/10 transition-all flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <CalendarCheck size={20}/>
                    {text.ctaBtnSchedule}
                </a>
            </div>
         </div>
      </div>

    </div>
  );
};

export default SST;